import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faUnlockAlt, faChalkboardTeacher, faUserTie, faCity } from "@fortawesome/free-solid-svg-icons";
import { Col, Row, Form, Card, Button, Container, InputGroup, Alert, Modal } from '@themesberg/react-bootstrap';
import { Link, useHistory } from 'react-router-dom';

import { Routes } from "../routes";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../contexts/RoleContext";
import BgImage from "../assets/img/illustrations/signin.svg";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [userRoles, setUserRoles] = useState([]);

  const { login } = useAuth();
  const { setSelectedRole } = useRole();
  const history = useHistory();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success && result.user) {
        // Verificar se usuário tem múltiplos papéis
        const roles = checkUserRoles(result.user);

        if (roles.length > 1) {
          // Tem múltiplos papéis - mostrar modal de seleção
          setUserRoles(roles);
          setShowRoleModal(true);
          setLoading(false);
        } else if (roles.length === 1) {
          // Tem apenas um papel - salvar e redirecionar
          localStorage.setItem('selectedRole', roles[0].role);
          history.push(Routes.DashboardOverview.path);
        } else {
          setError('Nenhum papel ativo encontrado para este usuário.');
          setLoading(false);
        }
      } else {
        setError(result.error || 'Erro ao fazer login. Verifique suas credenciais.');
        setLoading(false);
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor. Tente novamente.');
      setLoading(false);
    }
  };

  const checkUserRoles = (user) => {
    const uniqueRoles = [];
    const seenRoles = new Set();

    // Verificar papéis em escolas
    if (user?.school_users) {
      const activeSchoolUsers = user.school_users.filter(su => su.active);

      activeSchoolUsers.forEach(su => {
        if (!seenRoles.has(su.role)) {
          seenRoles.add(su.role);
          uniqueRoles.push({
            role: su.role,
            role_display: su.role_display,
            icon: su.role === 'TEACHER' ? faChalkboardTeacher : faUserTie,
            description: su.role === 'TEACHER'
              ? 'Acesse suas turmas, disciplinas e planos de aula'
              : 'Gerencie professores, alunos e configurações da escola'
          });
        }
      });
    }

    // Verificar papéis em municípios
    if (user?.municipality_users && user.municipality_users.length > 0) {
      const activeMunicipalityUsers = user.municipality_users.filter(mu => mu.active);

      if (activeMunicipalityUsers.length > 0 && !seenRoles.has('MUNICIPAL_SECRETARY')) {
        seenRoles.add('MUNICIPAL_SECRETARY');
        uniqueRoles.push({
          role: 'MUNICIPAL_SECRETARY',
          role_display: 'Secretário Municipal',
          icon: faCity,
          description: 'Gerencie todas as escolas e dados educacionais do município'
        });
      }
    }

    return uniqueRoles;
  };

  const handleRoleSelection = (role) => {
    // Definir papel no localStorage E no RoleContext
    localStorage.setItem('selectedRole', role);
    setSelectedRole(role);
    setShowRoleModal(false);

    // Pequeno delay para garantir que o contexto foi atualizado
    setTimeout(() => {
      history.push(Routes.DashboardOverview.path);
    }, 100);
  };

  return (
    <main>
      {/* Modal de Seleção de Papel */}
      <Modal show={showRoleModal} onHide={() => {}} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Selecione seu Papel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Você possui múltiplos papéis no sistema. Selecione como deseja acessar:
          </p>
          <Row className="g-3">
            {userRoles.map((roleData) => (
              <Col md={6} key={roleData.role}>
                <Card
                  className="text-center cursor-pointer h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRoleSelection(roleData.role)}
                >
                  <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
                    <FontAwesomeIcon
                      icon={roleData.icon}
                      size="3x"
                      className="text-primary mb-3"
                    />
                    <h5 className="mb-2">{roleData.role_display}</h5>
                    <p className="text-muted small mb-0">{roleData.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Modal.Body>
      </Modal>

      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <Row className="justify-content-center form-bg-image" style={{ backgroundImage: `url(${BgImage})` }}>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h3 className="mb-0">Login</h3>
                </div>

                {error && (
                  <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                  </Alert>
                )}

                <Form className="mt-4" onSubmit={handleSubmit}>
                  <Form.Group id="email" className="mb-4">
                    <Form.Label>E-mail</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUser} />
                      </InputGroup.Text>
                      <Form.Control
                        autoFocus
                        required
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group id="password" className="mb-4">
                    <Form.Label>Senha</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUnlockAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="password"
                        placeholder="Digite sua senha"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={loading}
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>
                </Form>
                <div className="d-flex justify-content-center align-items-center mt-4">
                  <Card.Link as={Link} to={Routes.ForgotPassword.path} className="small">
                    Esqueceu a senha?
                  </Card.Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Login;
